import React, { useEffect, useRef } from "react";
import { Grid, IconButton, Paper } from "@mui/material";
import { styled } from '@mui/material/styles';
import EnhancedTable from "../../../../../components/table";
import SettingsResults from "../components/taxonomy_results";
import Config from "../../../../../config";
import axios from "axios";
import CatalogTab from "../../catalog/components/catalog_tab";
import { AttributesColumn, DescriptionColumn, LastModifiedDate, OptionsColumn, PathColumn, SyncStatusColumn } from "../components/taxonomy_columns";
import Helpers from "../../../../../helpers";
import { ArrowBack } from "@mui/icons-material";
import { getAttributeSets as utilGetAttributeSets } from "../../../../../utils/utils";
import { pageContentGap, Header } from "../../../../../theme";

interface TaxonomyProps {
    setTitle?: (title: string, flag?: boolean) => void;
    history: {
        push: (path: string) => void;
        goBack: () => void;
        replace: (location: { pathname: string; search?: string }) => void;
        [key: string]: any;
    };
    location: { key?: string; pathname: string; search?: string; [key: string]: any };
}

const RootGrid = styled(Grid)({
    // 100vw - {Sidebar Width} - {Inner Padding} - {app-content Padding}
    // maxWidth: "calc(100vw - 220px - 28px - 2em)"
    display: "flex",
    flexDirection: "column",
    gap: pageContentGap,
});
const HeaderTypography = Header;
export { RootGrid, HeaderTypography };
const iconButtonSx = (theme: any) => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});
let cancelSource = axios.CancelToken.source();
const baseConfig = {
    selectable: false,
    savedSearchEnabled: true,
    order: 'asc',
    pageSize: 25,
    pageSizes: [25, 50, 100],
    refresh: false,
    term: "",
    facets: [],
    filter: null,
    filterInUrl: true
}
const attributeConfig = {
    ...baseConfig,
    title: `attributes`,
    // TODO: use function from utils
    getData: async () => {
        let config = { cancelToken: cancelSource.token }
        let response = await axios.get(Config.api + "/api/v2/ProductOnboardingCenter/Attributes", config);
        return response.data.Records;
    },
    columns: [
        {
            id: "Name", label: "Name", toggleable: false, sortable: true, searchable: true,
            getOptions: (data: any) => {
                if (data === undefined) return [];
                return data.map((a: any) => ({ value: a.Name, label: a.Name }));
            }
        },
        {
            id: "FriendlyName", label: "FriendlyName", toggleable: true, sortable: true, searchable: true,
            getOptions: (data: any) => {
                if (data === undefined) return [];
                return data.map((a: any) => ({ value: a.FriendlyName, label: a.FriendlyName }));
            }
        },
        {
            id: "Type", label: "Type", toggleable: true, sortable: true, searchable: true,
            getOptions: () => {
                return [{ label: "text", value: "text" },
                { label: "select", value: "select" },
                { label: "number", value: "number" },
                { label: "image", value: "image" },
                { label: "yesno", value: "yesno" },
                { label: "list", value: "list" },]
            }
        },
        {
            id: "Description", label: "Description", toggleable: true, allowStringOperators: true,
            template: (row: any, column: any, colIndex: number, rowIndex: number, addFilter: any) => <DescriptionColumn row={row} column={column}
                colIndex={colIndex}
                addFilter={addFilter}
                rowIndex={rowIndex} />
        },
        {
            id: "Options", label: "Options", toggleable: true, type: "array", hidden: true,
            width: "200px",
            template: (row: any, column: any, colIndex: number, rowIndex: number, addFilter: any) => <OptionsColumn row={row} column={column}
                colIndex={colIndex}
                addFilter={addFilter}
                rowIndex={rowIndex} />
        },
        {
            id: "Group", label: "Group", toggleable: true, sortable: true, searchable: true, hidden: true,
            getOptions: (data: any) => {
                if (data === undefined) return [];
                let dedupedData = Array.from(new Set(data.map((a: any) => a.Group)));
                return dedupedData.map((a: any) => ({ value: a, label: a }));
            }
        },
        {
            id: "System", label: "System", toggleable: true, sortable: true, type: "bool", hidden: true,
        },
        {
            id: "SyncStatus",
            label: "Sync Status",
            hidden: false,
            toggleable: true,
            filterable: false,
            sortable: true,
            template: (row: any, column: any, colIndex: number, rowIndex: number, addFilter: any) => <SyncStatusColumn row={row} column={column}
                colIndex={colIndex}
                addFilter={addFilter}
                rowIndex={rowIndex} />
        },
        {
            id: "LastModified", label: "LastModified", toggleable: true, sortable: true, type: "date",
            template: (row: any, column: any, colIndex: number, rowIndex: number, addFilter: any) => <LastModifiedDate row={row} column={column}
                colIndex={colIndex}
                addFilter={addFilter}
                rowIndex={rowIndex} />
        },
        {
            id: "Hidden", label: "Hidden", hidden: true, toggleable: false,
        },
        {
            id: "HiddenFromPartner", label: "HiddenFromPartner", hidden: true, toggleable: false,
        },
        { empty: true, hidden: true, searchable: false, }, // Header for action col 1
        { empty: true, hidden: true, searchable: false, } // Header for action col 2
    ],
    orderBy: "Name"
}
const attributeSetConfig = {
    ...baseConfig,
    title: `attributeSets`,
    // TODO: use function from utils
    getData: async () => {
        let attributeSets = await utilGetAttributeSets(undefined, undefined, cancelSource, true);
        if (attributeSets.errorMessage) return [];
        else return attributeSets;
    },
    columns: [
        {
            id: "Name", label: "Name", toggleable: false, sortable: true, searchable: true,
            getOptions: (data: any) => {
                if (data === undefined) return [];
                return data.map((a: any) => ({ value: a.Name, label: a.Name }));
            }
        },
        {
            id: "FriendlyName", label: "FriendlyName", toggleable: true, sortable: true, searchable: true,
            getOptions: (data: any) => {
                if (data === undefined) return [];
                return data.map((a: any) => ({ value: a.FriendlyName, label: a.FriendlyName }));
            }
        },
        {
            id: "Attributes", label: "Attributes", toggleable: true, type: "array", hidden: true, filterable: false,
            width: "220px",
            template: (row: any, column: any, colIndex: number, rowIndex: number, addFilter: any) => <AttributesColumn row={row} column={column}
                colIndex={colIndex}
                addFilter={addFilter}
                rowIndex={rowIndex} />
        },
        {
            id: "System", label: "System", toggleable: true, sortable: true, type: "bool", hidden: true,
        },
        {
            id: "DestinationId", label: "DestinationId", toggleable: true, sortable: true, hidden: true,
        },
        {
            id: "Tags", label: "Tags", toggleable: true, sortable: true, allowStringOperators: true, searchable: true,
        },
        {
            id: "CustomAttributes",
            label: "CustomAttributes",
            toggleable: true,
            sortable: true,
            defaultToggled: true,
            hidden: true
        },
        {
            id: "SyncStatus",
            label: "Sync Status",
            hidden: false,
            toggleable: true,
            filterable: false,
            sortable: true,
            template: (row: any, column: any, colIndex: number, rowIndex: number, addFilter: any) => <SyncStatusColumn row={row} column={column}
                colIndex={colIndex}
                addFilter={addFilter}
                rowIndex={rowIndex} />
        },
        {
            id: "LastModified", label: "LastModified", toggleable: true, sortable: true, type: "date",
            template: (row: any, column: any, colIndex: number, rowIndex: number, addFilter: any) => <LastModifiedDate row={row} column={column}
                colIndex={colIndex}
                addFilter={addFilter}
                rowIndex={rowIndex} />
        },
        {
            id: "Hidden", label: "Hidden", hidden: true, toggleable: false,
        },
        {
            id: "HiddenFromPartner", label: "HiddenFromPartner", hidden: true, toggleable: false,
        },
        { empty: true, hidden: true }, // Header for action col 1
        { empty: true, hidden: true } // Header for action col 2
    ],
    orderBy: "System",
    order: "desc"
}
const categoriesConfig = {
    ...baseConfig,
    title: `categories`,
    // TODO: use function from utils
    getData: async () => {
        let config = { cancelToken: cancelSource.token }
        let response = await axios.get(Config.api + "/api/v2/ProductOnboardingCenter/Categories", config);
        return response.data.Records;
    },
    columns: [
        {
            id: "Id", label: "Id", toggleable: false, hidden: true, searchable: true
        },
        {
            id: "Path", label: "Path", toggleable: true, sortable: true, searchable: true, allowStringOperators: true,
            template: (row: any, column: any, colIndex: number, rowIndex: number, addFilter: any) => <PathColumn row={row} column={column}
                colIndex={colIndex}
                addFilter={addFilter}
                rowIndex={rowIndex} />
        },
        {
            id: "AttributeSet", label: "AttributeSet", toggleable: true, sortable: true, searchable: true,
            getOptions: async () => {
                let attributeSets = await utilGetAttributeSets(false, undefined, cancelSource, true);
                return attributeSets.map((a: any) => ({ value: a.Name, label: a.FriendlyName }));
            },
        },
        {
            id: "Name", label: "Name", toggleable: false, sortable: true, hidden: true, searchable: true
        },
        {
            id: "Level", label: "Level", toggleable: true, sortable: true, type: "number",
        },
        {
            id: "Children", label: "Children", toggleable: true, type: "number",
        },
        {
            id: "SyncStatus",
            label: "Sync Status",
            hidden: false,
            toggleable: true,
            filterable: false,
            sortable: true,
            template: (row: any, column: any, colIndex: number, rowIndex: number, addFilter: any) => <SyncStatusColumn row={row} column={column}
                colIndex={colIndex}
                addFilter={addFilter}
                rowIndex={rowIndex} />
        },
        {
            id: "LastModified", label: "LastModified", toggleable: true, sortable: true, type: "date",
            template: (row: any, column: any, colIndex: number, rowIndex: number, addFilter: any) => <LastModifiedDate row={row} column={column}
                colIndex={colIndex}
                addFilter={addFilter}
                rowIndex={rowIndex} />
        },
        {
            id: "Hidden", label: "Hidden", hidden: true, toggleable: false,
        },
        {
            id: "HiddenFromPartner", label: "HiddenFromPartner", hidden: true, toggleable: false,
        },
        { empty: true, hidden: true }, // Header for action col 1
        { empty: true, hidden: true } // Header for action col 2
    ],
    orderBy: "Path"
}
export default function Taxonomy(props: TaxonomyProps) {
    const { setTitle } = props;
    const urlSetting = new Helpers().getUrlObject('setting') || "categories";
    const [currentSetting, setCurrentSetting] = React.useState(urlSetting);
    const [config, setConfig] = React.useState<any>(
        urlSetting === "attributeSets" ? attributeSetConfig
            : urlSetting === "attributes" ? attributeConfig
                : categoriesConfig);
    const configRef = useRef<any>(undefined);
    configRef.current = config;
    //region Functions
    const getData = (callback: (...args: any[]) => Promise<any>, columns: any[]) => {
        /* getData is defined in configuration, however, in the situation we want to apply outside parameters/redefine
           this function we create a callback. In this case, on component creation we use a useEffect to update the
           definition to this function while passing in the previous getData function. Then we use the previous
           getData function to get any data then perform operations on it with outside information. In this case, term. */
        return async () => {
            let data = await callback.apply(null, [configRef.current.term]);
            let term = configRef.current.term;
            if (term !== "" && term != null) {
                let split = term.toLowerCase().split(" ");
                let fields = columns.filter((column: any) => column.searchable).map((c: any) => c.id);
                data = data.filter((r: any) =>
                    split.every((s: string) =>
                        fields.filter((f: string) => r[f] && r[f].toLowerCase().indexOf(s) > -1).length > 0
                    )
                );
            }
            return data;
        };
    }
    const resetCancelSource = () => {
        cancelSource = axios.CancelToken.source();
    }
    const updateSetting = (setting: string) => {
        // Clear any existing filters and saved search state when switching tabs
        if (setting === "categories")
            setConfig((prevState: any) => ({
                ...categoriesConfig,
                getData: getData(categoriesConfig.getData, categoriesConfig.columns),
                refresh: !prevState.refresh,
                filter: null,
                facets: [],
                term: ""
            }));
        else if (setting === "attributes")
            setConfig((prevState: any) => ({
                ...attributeConfig,
                getData: getData(attributeConfig.getData, attributeConfig.columns),
                refresh: !prevState.refresh,
                filter: null,
                facets: [],
                term: ""
            }));
        else
            setConfig((prevState: any) => ({
                ...attributeSetConfig,
                getData: getData(attributeSetConfig.getData, attributeSetConfig.columns),
                refresh: !prevState.refresh,
                filter: null,
                facets: [],
                term: ""
            }));
        // Clear URL parameters to ensure no stale filter state
        props.history.replace({ pathname: props.location.pathname, search: "" });
        new Helpers().setUrlObject('setting', setting, props.history, props.location);
        setCurrentSetting(setting);
    }
    //endregion
    useEffect(() => {
        if (setTitle) {
            switch (currentSetting) {
                case "categories":
                    setTitle('POC Category Settings', true);
                    break;
                case "attributes":
                    setTitle('POC Attributes Settings', true);
                    break;
                case "attributeSets":
                    setTitle('POC Attribute Sets Settings', true);
                    break;
                default:
                    setTitle('POC Settings', true);
            }
        }
    }, [currentSetting, setTitle]);
    useEffect(() => {
        updateSetting(urlSetting);
        return () => {
            cancelSource.cancel("Component unmounted");
            resetCancelSource();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <RootGrid container
            style={{ gap: pageContentGap, padding: "24px 14px 24px 14px", position: "relative" }}>
            <Grid container justifyContent={"space-between"} direction={"row"}>
                <Grid item container justifyContent={"flex-start"} alignItems={"center"} style={{ gap: pageContentGap, width: "100%" }}>
                    <IconButton
                        sx={iconButtonSx}
                        style={{ height: "36px", width: "36px", position: 'relative', zIndex: 1 }}
                        onClick={(e) => {
                            e.preventDefault();
                            if (props.location.key === undefined)
                                props.history.push("/productonboardingcenter/catalog");
                            else
                                props.history.goBack();
                        }}
                        size="large">
                        <ArrowBack style={{ height: "18px", width: "18px" }} />
                    </IconButton>
                    <HeaderTypography>
                        Taxonomy
                    </HeaderTypography>
                </Grid>
            </Grid>
            <Grid container style={{ gap: pageContentGap }}>
                <Paper>
                    <CatalogTab title={"Categories"} description={"View and manage Categories"}
                        selected={currentSetting === "categories"}
                        onClick={() => {
                            updateSetting("categories");
                        }}
                    />
                </Paper>
                <Paper>
                    <CatalogTab title={"Attributes"} description={"View and manage Attributes"}
                        selected={currentSetting === "attributes"}
                        onClick={() => {
                            updateSetting("attributes");
                        }}
                    />
                </Paper>
                <Paper>
                    <CatalogTab title={"Attribute Sets"} description={"View and manage Attribute sets"}
                        selected={currentSetting === "attributeSets"}
                        onClick={() => {
                            updateSetting("attributeSets");
                        }}
                    />
                </Paper>
            </Grid>
            <EnhancedTable config={config} history={props.history} location={props.location}
                template={(table: any) =>
                    <SettingsResults history={props.history} location={props.location}
                        currentSetting={currentSetting} table={table}
                        config={config} setConfig={setConfig}
                        cancelSource={cancelSource} resetCancelSource={resetCancelSource}
                    />
                } />
        </RootGrid>
    );
}
